/** Paging and timing rules for the auto-sliding spotlight strip, kept pure so node-only vitest can pin them. */

export const SLIDE_MS = 8000

/** Keep a page index valid when the page count changes under it (a resize, or data arriving late). */
export function clampIndex(index: number, count: number): number {
  if (count <= 0 || index < 0) return 0
  return index >= count ? count - 1 : index
}

export function nextIndex(index: number, count: number): number {
  return count <= 0 ? 0 : (index + 1) % count
}

export function prevIndex(index: number, count: number): number {
  return count <= 0 ? 0 : (index - 1 + count) % count
}

/** Advance on its own only when nothing asks it to hold: reduced motion, the viewer's pause, hover or focus inside (WCAG 2.2.2), a hidden tab, or a single page. */
export function shouldAutoAdvance(state: {
  reducedMotion: boolean
  paused: boolean
  hovered: boolean
  focused: boolean
  hidden: boolean
  pages: number
}): boolean {
  if (state.reducedMotion || state.paused || state.pages <= 1) return false
  return !state.hovered && !state.focused && !state.hidden
}

/** Size of a card in a packed slide: small figures share a slide, a wide chart takes one of its own. */
export type SlideWeight = 1 | 2 | 4

/** Weight units one slide holds at this width: about four chunks where four fit (Miller's Law), down to one on a phone. */
export function packCapacity(width: number, unit = 240): number {
  return Math.max(1, Math.min(4, Math.floor(width / unit)))
}

/** Cards grouped into slides in their given order, a new slide starting when the next card does not fit or opens a new group, so the reading order never changes; a card wider than a slide gets one alone. */
export function packSlides(weights: readonly number[], capacity: number, groups?: readonly (string | undefined)[]): number[][] {
  const cap = Math.max(1, capacity)
  const slides: { items: number[]; used: number }[] = []
  weights.forEach((w, i) => {
    const size = Math.min(Math.max(1, w), cap)
    const last = slides[slides.length - 1]
    if (last && last.used + size <= cap && groups?.[i] === groups?.[i - 1]) {
      last.items.push(i)
      last.used += size
    } else slides.push({ items: [i], used: size })
  })
  return slides.map((s) => s.items)
}

/** The strip's height while a page shows: that page's own, or none (the tallest page) until it is measured, so an unmeasured page is never clipped. */
export function trackHeight(heights: readonly number[], page: number): number | undefined {
  const h = heights[page]
  return h !== undefined && h > 0 ? h : undefined
}
